import {  useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShellSnippet } from '@/shared/components/ShellSnippet'
import { HclSnippet } from '@/shared/components/HclSnippet'
import type { OrgItem } from '@/lib/apiTypes'
import { providerRegistryHost, providerTokenEnvVar } from '@/lib/format'
import { s } from '@/features/integrations/guideStyles'
import type { GuideId } from '@/features/integrations/guides'
import { PlatformTabs, type Platform } from '@/features/integrations/components/PlatformTabs'
import { CurlSecretNote, CliCiNote } from '@/features/integrations/components/GuideNotes'
import {   CLI_INSTALL_GITHUB, CLI_GITLAB_SETUP, CLI_GITLAB_ID_TOKENS, TOKEN_EXCHANGE_CLI } from '@/features/integrations/snippets'

// ── Using Providers ───────────────────────────────────────────────────────────
export function UsingProvidersGuide({ org, token, onSwitchGuide }: { org: OrgItem | null; token: string | null; onSwitchGuide: (id: GuideId) => void }) {
  const navigate = useNavigate()
  const [platform, setPlatform] = useState<Platform>('cli')
  const tokenVal = token ?? '<bot token>'
  const orgSlug  = org?.slug ?? '<org-slug>'

  // Providers are org-scoped by SUBDOMAIN host: <slug>.registry.terramantle.dev. The org is the
  // host, so tofu resolves it deterministically (no oldest-membership guess) - the source keeps the
  // standard <namespace>/<type> form. regHost falls back to a <org-slug> placeholder when no org is
  // selected; tokenVar is keyed on the full subdomain host.
  const regHost = providerRegistryHost(org?.slug)
  const tokenVar = providerTokenEnvVar(regHost)

  const requiredProviders = `terraform {
  required_providers {
    aws = {
      source  = "${regHost}/hashicorp/aws"
      version = "~> 5.0"
    }
  }
}`

  const terraformrc = `# ~/.terraformrc  (Windows: %APPDATA%\\terraform.rc)
credentials "${regHost}" {
  token = "${tokenVal}"
}`

  const cliSnippet = `terramantle auth login

# What your org serves, with Trust verdicts inline
terramantle providers ls
terramantle providers ls --at-risk
terramantle providers show hashicorp/aws

# Exports ${tokenVar} for you - no ~/.terraformrc needed
eval "$(terramantle auth env --terraform)"

tofu init   # or: terraform init`

  const curlSnippet = `${TOKEN_EXCHANGE_CLI}

# Option A - CLI config (~/.terraformrc), best for local / interactive use
cat > ~/.terraformrc <<EOF
credentials "${regHost}" {
  token = "${tokenVal}"
}
EOF

# Option B - environment variable, best for CI
#   The host's dots/dashes become underscores.
export ${tokenVar}=${tokenVal}

tofu init   # or: terraform init`

  const githubSnippet = `name: terraform

on:
  push:
    branches: [main]

permissions:
  contents: read
  id-token: write   # lets terramantle use the runner's ambient OIDC token

jobs:
  apply:
    runs-on: ubuntu-latest
    env:
      TERRAMANTLE_ORG: ${orgSlug}
    steps:
      - uses: actions/checkout@v4
      - uses: opentofu/setup-opentofu@v1

${CLI_INSTALL_GITHUB}

      - name: Terraform init & apply
        run: |
          # Sets ${tokenVar}, which tofu sends as a Bearer token
          eval "$(terramantle auth env --terraform)"
          tofu init
          tofu apply -auto-approve`

  const gitlabSnippet = `stages:
  - deploy

apply:
  stage: deploy
  image: ubuntu:24.04
${CLI_GITLAB_ID_TOKENS}
  variables:
    TERRAMANTLE_ORG: ${orgSlug}
${CLI_GITLAB_SETUP}
  script:
    - eval "$(terramantle auth env --terraform)"
    - tofu init
    - tofu apply -auto-approve
  only:
    - main`

  return (
    <div>
      <h3 style={s.guideTitle}>Using Providers</h3>
      <p style={s.para}>
        Terramantle serves providers to your org from its own registry. Reference a provider with
        a standard source address and Terramantle serves the upstream versions (fetched, cached and
        scanned on first use) and, on Business+, terramantle's own builds alongside them,
        selectable by exact pin. The org is the registry subdomain host, so every provider source
        resolves to your org deterministically.
      </p>

      <h3 style={s.subTitle}>1. Manage the provider</h3>
      <p style={s.para}>
        Only providers your organisation manages are served - everything else returns{' '}
        <code style={s.code}>403</code>. Manage one in{' '}
        <button style={s.linkBtn} onClick={() => navigate(org ? `/org/${org.slug}/providers` : '/')}>Providers</button>{' '}
        with the namespace (e.g. <code style={s.code}>hashicorp</code>) and type (e.g. <code style={s.code}>aws</code>).
        A managed provider serves by default; the Off / Serve / Block control on its row changes that.
      </p>

      <h3 style={s.subTitle}>2. Declare the provider</h3>
      <p style={s.para}>
        Source addresses are <code style={s.code}>{regHost}/&lt;namespace&gt;/&lt;type&gt;</code> -
        your org's registry subdomain, then the same namespace and type you would use upstream.
        {!org?.slug && ' (Select an org to fill in your subdomain.)'}
      </p>
      <HclSnippet code={requiredProviders} copyText={requiredProviders} />

      <h3 style={s.subTitle}>3. Authenticate and initialise</h3>
      <p style={s.para}>
        The <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-getting-started')}>Terramantle CLI</button>{' '}
        exports <code style={s.code}>{tokenVar}</code> for you and lists what the org serves. Setting the variable by hand
        works too, as does a <code style={s.code}>credentials</code> block in your CLI config - both are read
        natively by Terraform and OpenTofu. No <code style={s.code}>TF_HTTP_PASSWORD</code> here, that is for the
        state backend only.
      </p>
      <PlatformTabs value={platform} onChange={setPlatform} />
      {platform === 'cli'    && <ShellSnippet code={cliSnippet}    language="bash" />}
      {platform === 'curl'   && <ShellSnippet code={curlSnippet}   language="bash" />}
      {platform === 'github' && <ShellSnippet code={githubSnippet} language="yaml" />}
      {platform === 'gitlab' && <ShellSnippet code={gitlabSnippet} language="yaml" />}

      {platform === 'curl' && (
        <>
          <p style={{ ...s.para, marginTop: 10 }}>For local use, a <code style={s.code}>credentials</code> block is the alternative to the env var:</p>
          <HclSnippet code={terraformrc} copyText={terraformrc} />
          <CurlSecretNote />
        </>
      )}
      {(platform === 'github' || platform === 'gitlab') && <CliCiNote onSwitchGuide={onSwitchGuide} />}

      <h3 style={s.subTitle}>Notes</h3>
      <ul style={s.list}>
        <li>The first request for a version fetches it from upstream, caches the binary and queues a scan - later requests are served from the cache.</li>
        <li>Each managed provider pins an upstream: <code style={s.code}>registry.terraform.io</code> or <code style={s.code}>registry.opentofu.org</code>.</li>
        <li>terramantle builds are published as <code style={s.code}>&lt;base&gt;-0.0.&lt;rev&gt;</code> prereleases. tofu drops prereleases from a range constraint, so you can only <em>name</em> one with an exact <code style={s.code}>version = "..."</code> pin - but on Business and above the registry answers a download of the base version with the build, and your lockfile then records its hashes.</li>
        <li>Serving providers through the org registry requires a Business plan or above; otherwise requests return <code style={s.code}>402</code>. Managing and discovering providers is available on every plan.</li>
      </ul>
    </div>
  )
}
